import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { CompetitorProperty } from '~/types/services/priceParrot';

import CompetitorOfferSiteLink from './CompetitorOfferSiteLink';
import {
  competitorOfferPath,
  formatAmountWithAud,
  formatDate,
  formatStayPrice,
  lowestStayOf,
  plural,
  stayBasis,
  supplierKey,
  supplierLabel,
  taxBasisLabel,
} from './utils';

interface Props {
  suppliers: Array<CompetitorProperty>;
}

// Each vendor's deal summary from its latest collection; room options live on the detail page.
export default function CompetitorOfferSuppliersPanel({ suppliers }: Props) {
  return (
    <Box py={2} px={7}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Vendor</TableCell>
            <TableCell>Lead price</TableCell>
            <TableCell>Was price</TableCell>
            <TableCell>Discount</TableCell>
            <TableCell>Lowest stay</TableCell>
            <TableCell>Sale dates (as published)</TableCell>
            <TableCell>Collected</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {suppliers.map((supplier) => (
            <TableRow key={supplierKey(supplier)}>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" fontWeight="medium">
                    {supplierLabel(supplier, suppliers)}
                  </Typography>
                  <CompetitorOfferSiteLink property={supplier} iconOnly />
                </Stack>
                <Typography variant="caption" color="text.secondary" component="div">
                  {plural(supplier.optionCount, 'room option')}
                </Typography>
              </TableCell>
              <TableCell>
                {formatAmountWithAud(supplier.leadPrice, supplier.leadCurrency, supplier.leadPriceAud)}
                <Typography variant="caption" color="text.secondary" component="div">
                  {taxBasisLabel(supplier.leadPriceIncludesTax)}
                </Typography>
              </TableCell>
              <TableCell>
                {formatAmountWithAud(supplier.wasPrice, supplier.leadCurrency, supplier.wasPriceAud)}
              </TableCell>
              <TableCell>{supplier.discountPct !== undefined ? `${supplier.discountPct}% off` : '—'}</TableCell>
              <TableCell>
                {formatStayPrice(lowestStayOf(supplier))}
                <Typography variant="caption" color="text.secondary" component="div">
                  {stayBasis(supplier.lowestStayNights, supplier.lowestStayAdults, supplier.lowestStayCheckIn)}
                </Typography>
              </TableCell>
              <TableCell>
                {formatDate(supplier.saleStart)} – {formatDate(supplier.saleEnd)}
              </TableCell>
              <TableCell>
                {formatDate(supplier.firstCollectedAt)} – {formatDate(supplier.lastCollectedAt)}
                {supplier.collectionCount !== undefined && (
                  <Typography variant="caption" color="text.secondary" component="div">
                    {plural(supplier.collectionCount, 'collection')}
                  </Typography>
                )}
              </TableCell>
              <TableCell align="right">
                <Button size="small" component={Link} to={competitorOfferPath(supplier)}>
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
